import { useStripe } from '@stripe/react-stripe-js'
import { PaymentRequest } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'

import { reportErrorContext } from '@/lib/bugsnag'
import { getStripePlatformAccountCountryCodeForCurrency } from '@/utils/stripe'

export type PaymentRequestDevice = {
  googlePay?: boolean
  applePay?: boolean
}

export type PaymentRequestCheckResult = {
  paymentRequest: PaymentRequest
  device: PaymentRequestDevice
}

/**
 * Apple / Google Pay
 * Won't load if Stripe is not enabled
 */
export const useInitStripePaymentRequest = ({
  totalInCents,
  currency,
  hasStripe,
}: {
  totalInCents: number
  currency: string
  hasStripe: boolean
}): {
  paymentRequestCheckResult?: PaymentRequestCheckResult | null
  paymentRequestFetching: boolean
} => {
  const stripe = useStripe()

  const [paymentRequestCheckResult, setPaymentRequestCheckResult] =
    useState<PaymentRequestCheckResult>()
  const [paymentRequestFetching, setPaymentRequestFetching] = useState(false)

  useEffect(() => {
    async function checkPaymentRequestAvailable() {
      try {
        if (!hasStripe || !stripe) {
          return
        }
        setPaymentRequestFetching(true)
        const paymentRequest = stripe.paymentRequest({
          country: getStripePlatformAccountCountryCodeForCurrency(currency),
          currency: currency.toLowerCase(),
          total: {
            label: 'me&u',
            amount: totalInCents,
          },
        })

        const result = await paymentRequest.canMakePayment()
        if (!result) {
          return
        }
        if (result.applePay || result.googlePay) {
          setPaymentRequestCheckResult({
            device: result.applePay ? { applePay: true } : { googlePay: true },
            paymentRequest,
          })
        }
      } catch (error) {
        reportErrorContext(error, 'stripe')
      } finally {
        setPaymentRequestFetching(false)
      }
    }

    if (!paymentRequestCheckResult) {
      void checkPaymentRequestAvailable()
    }
  }, [currency, stripe, totalInCents, paymentRequestCheckResult, hasStripe])

  return {
    paymentRequestCheckResult,
    paymentRequestFetching,
  }
}
